import { Box, Typography } from '@mui/material'
import { Table } from 'antd'
import dayjs from 'dayjs'
import React, { forwardRef, RefObject } from 'react'

import CircularLoading from '../../../../../components/CircularProgress'
import { DATE_FORMAT_INTERNATIONAL } from '../../../../../constants/global'
import { IMerchant } from '../../../../../types/merchant'
import {
  ignoreDateTimezone,
  mergeObjects,
  numberFormat,
} from '../../../../../utils'
import { ICostDailyReport, IMerchantDailyReport } from '../index'
import { IFilterMapped } from './DailySalesReportFilter'
import { DailySalesReportOrderTableModal } from './DailySalesReportOrderTableModal'
import { fontSize } from 'styled-system'
import { number } from 'yup'
import { useUser } from '../../../../../hooks/useUser'

export interface IDailySalesTableProps {
  merchantDailyReports?: IMerchantDailyReport[]
  costDailyReport?: ICostDailyReport[]
  isLoading: boolean
  merchant?: IMerchant
  filter: IFilterMapped
  tableRef?: RefObject<any>
}

export interface IReport extends IMerchantDailyReport {
  costAmountTotal: number
  adsActive: number
  adsInactive: number
  shipping: number
  income: number
  adsPercent: number
}

const DailySalesReportTable = forwardRef<
  HTMLTableElement,
  IDailySalesTableProps
>((props: IDailySalesTableProps, ref) => {
  const { merchantDailyReports, costDailyReport, merchant, filter, tableRef } =
    props

  const { user } = useUser()
  const [isOpen, setIsOpen] = React.useState(false)
  const [date, setDate] = React.useState({ startDate: '', endDate: '' })
  const {
    commissionType = '',
    expense = 1,
    calTax = 0,
    codPercent = 0,
    shippingCost = 1,
  } = filter
  const commissionTypeName = commissionType === 'all' ? '' : commissionType

  const costObject = costDailyReport?.reduce(
    (acc: any, costDailyReportItem: ICostDailyReport) => {
      const date = dayjs(ignoreDateTimezone(costDailyReportItem.date)).format(
        DATE_FORMAT_INTERNATIONAL,
      )
      acc[date] = {
        costAmountTotal: costDailyReportItem.amount,
        adsActive: costDailyReportItem.adsActive,
        adsInactive: costDailyReportItem.adsInactive,
        shipping: costDailyReportItem.shipping,
      }
      return acc
    },
    {},
  )

  const merchantObject = merchantDailyReports?.reduce(
    (acc: any, merchantDailyReportItem: IMerchantDailyReport) => {
      const date = dayjs(
        ignoreDateTimezone(merchantDailyReportItem.date),
      ).format(DATE_FORMAT_INTERNATIONAL)
      acc[date] = merchantDailyReportItem
      return acc
    },
    {},
  )

  const reports = mergeObjects(costObject, merchantObject)

  const reportsArray: IReport[] = Object.keys(reports)
    .map((date: string, index: number) => {
      const report = reports[date]
      const income =
        (report.bankTotal || 0) + (report.codTotal || 0)
          ? (report.bankTotal || 0) + (report.codTotal || 0)
          : 0
      const adsPercent =
        income > 0 ? ((report?.adsActive ?? 0) / income) * 100 : 0
      return {
        ...reports[date],
        date,
        income,
        adsPercent,
      }
    })
    .sort((a, b) => {
      const dateA: Date = new Date(a.date.split('/').reverse().join('-'))
      const dateB: Date = new Date(b.date.split('/').reverse().join('-'))
      return dateB.getTime() - dateA.getTime()
    })

  const totalSumOrder = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.count) || 0),
    0,
  )
  const totalSumUnit = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.unit) || 0),
    0,
  )
  const totalSumCodTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.codTotal) || 0),
    0,
  )
  const totalSumCodCount = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.codCount) || 0),
    0,
  )
  const totalSumBankTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.bankTotal) || 0),
    0,
  )
  const totalSumBankCount = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.bankCount) || 0),
    0,
  )
  const totalSumTax = reportsArray.reduce(
    (acc, item: IReport) =>
      acc +
      (Number(item.total) || 0) * ((merchant?.config?.taxPercent || 0) / 100),
    0,
  )
  const totalSumShippingCostFix = reportsArray.reduce(
    (acc, item: IReport) =>
      acc +
      (Number(item.count) || 0) * (merchant?.config?.shippingCostFix || 0),
    0,
  )
  const totalSumPercentCod = reportsArray.reduce(
    (acc, item: IReport) =>
      acc +
      (Number(item.codTotal) || 0) *
      ((merchant?.config?.codPercent || 0) / 100),
    0,
  )

  const totalSumAdsActive = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.adsActive) || 0),
    0,
  )
  const totalSumAdsInactive = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.adsInactive) || 0),
    0,
  )

  const totalSumShipping = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.shipping) || 0),
    0,
  )

  const totalIncome = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.income) || 0),
    0,
  )

  const totalAdsPercent =
    totalIncome > 0 ? (totalSumAdsActive / totalIncome) * 100 : 0
  //console.log('totalSumPercentCod', totalSumPercentCod)

  const totalSumReturnPercent = reportsArray.reduce(
    (acc, item: IReport) =>
      acc +
      (item.codTotal ?? 0) * ((merchant?.config?.returnPercent || 0) / 100),
    0,
  )
  const totalSumCostTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.costTotal) || 0),
    0,
  )
  const totalSumCommission = reportsArray.reduce((acc, item: IReport) => {
    const comission = item?.[commissionTypeName as keyof IReport] ?? 0
    acc = acc + (Number(comission) || 0)
    return acc
  }, 0)
  const totalSumSubTotal = totalSumCodTotal + totalSumBankTotal
  const totalSumCostAmountTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.costAmountTotal) || 0),
    0,
  )
  const totalSumTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.total) || 0),
    0,
  )

  const totalUpsale = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.upsaleTotal) || 0),
    0,
  )

  const totalUnsuccessUpsale = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.unsuccessUpSaleTotal) || 0),
    0,
  )

  const totalSumTaxAndReturnCost = totalSumTax + totalSumReturnPercent
  let totalSumCost = reportsArray.reduce((acc, item: IReport) => {
    const comission = item?.[commissionTypeName as keyof IReport] ?? 0
    let amount =
      acc +
      (Number(
        (item.costTotal || 0) +
        (expense === 1 ? item.costAmountTotal || 0 : 0) +
        (Number(comission) || 0),
      ) || 0)

    return amount
  }, 0)
  if (calTax === '1') {
    totalSumCost += totalSumTaxAndReturnCost
  }
  if (codPercent == 1) {
    totalSumCost += totalSumPercentCod
  }
  if (shippingCost === 1) {
    totalSumCost += totalSumShippingCostFix
  }
  const totalSumTotalAll = totalSumSubTotal - totalSumCost

  // console.log('totalSumTotalAll', totalSumTotalAll)
  // console.log('totalSumSubTotal', totalSumSubTotal)
  // console.log('totalSumCost', totalSumCost)

  const columns: any[] = [
    {
      title: 'วันที่',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      align: 'center' as const,
      render: (date: string, report: IReport) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {date}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'จำนวนสั่งซื้อ',
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'center' as const,
      render: (count: number, report: IReport) => (
        <Typography
          variant="subtitle2"
          color="primary"
          sx={{ cursor: 'pointer' }}
          textAlign={'center'}
          onClick={() => {
            setIsOpen(true)
            setDate({
              startDate: dayjs(report.date).format('YYYY-MM-DD:00:00:00'),
              endDate: dayjs(report.date).format('YYYY-MM-DD:23:59:59'),
            })
          }}
        >
          {count
            ? count.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'unit',
      key: 'unit',
      width: 75,
      align: 'center' as const,
      render: (unit: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {unit
            ? unit.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอด COD',
      dataIndex: 'codTotal',
      key: 'codTotal',
      width: 80,
      align: 'center' as const,
      render: (codTotal: number) => (
        <Typography variant="subtitle2" color="green" textAlign={'center'}>
          {codTotal
            ? codTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวน COD',
      dataIndex: 'codCount',
      key: 'codCount',
      width: 95,
      align: 'center' as const,
      render: (codCount: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {codCount
            ? codCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอดโอน',
      dataIndex: 'bankTotal',
      key: 'bankTotal',
      width: 70,
      align: 'center' as const,
      render: (bankTotal: number) => (
        <Typography variant="subtitle2" color="green" textAlign={'center'}>
          {bankTotal
            ? bankTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวนโอน',
      dataIndex: 'bankCount',
      key: 'bankCount',
      width: 85,
      align: 'center' as const,
      render: (bankCount: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {bankCount
            ? bankCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'UpSale',
      dataIndex: 'codTotal',
      key: 'codTotal',
      width: 80,
      align: 'center' as const,
      render: (_: any, report: IReport) => (
        <Box display={'flex'} justifyContent={'center'} gap={0.5}>
          <Typography variant="subtitle2" color="green" textAlign={'center'}>
            {report.upsaleTotal ?? 0}
          </Typography>
          <div>/</div>
          <Typography variant="subtitle2" color="red" textAlign={'center'}>
            {report.unsuccessUpSaleTotal ?? 0}
          </Typography>
        </Box>
      ),
    },
    {
      title: `ค่าภาษี ${merchant?.config?.taxPercent! || 0}%`,
      dataIndex: 'total',
      key: 'total',
      width: 90,
      align: 'center' as const,
      render: (total: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat(
            (total || 0) * ((merchant?.config?.taxPercent! || 0) / 100) * -1,
          )}
        </Typography>
      ),
    },
    {
      title: 'ค่าส่ง',
      dataIndex: 'count',
      key: 'count',
      width: 70,
      align: 'center' as const,
      render: (count: number = 0) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat(
            (count || 0) * (merchant?.config.shippingCostFix || 0) * -1,
          )}
        </Typography>
      ),
    },
    {
      title: `${merchant?.config?.codPercent || 0}% COD`,
      dataIndex: 'codTotal',
      key: 'codTotal',
      width: 85,
      align: 'center' as const,
      render: (codTotal: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat(
            (((merchant?.config?.codPercent || 0) * (codTotal || 0)) / 100) *
            -1,
          )}
        </Typography>
      ),
    },
    {
      title: `${merchant?.config?.returnPercent || 0}% ตีกลับ`,
      dataIndex: 'count',
      key: 'count',
      width: 85,
      align: 'center' as const,
      render: (_: number, report: IReport) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat(
            (((merchant?.config?.returnPercent || 0) * (report.codTotal || 0)) /
              100) *
            -1,
          )}
        </Typography>
      ),
    },
    {
      title: 'ต้นทุนสินค้า',
      dataIndex: 'costTotal',
      key: 'costTotal',
      width: 90,
      align: 'center' as const,
      render: (costTotal: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {user?.isNotAllowSeeCost ? '-' : numberFormat((costTotal || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: 'ค่าใช้จ่าย',
      dataIndex: 'costAmountTotal',
      key: 'costAmountTotal',
      width: 70,
      align: 'center' as const,
      render: (costAmountTotal: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat((costAmountTotal || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: 'ค่าขนส่ง',
      dataIndex: 'shipping',
      key: 'shipping',
      width: 70,
      align: 'center' as const,
      hidden: filter.showAdsShipping !== 1,
      render: (shipping: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat((shipping || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: 'แอดติด',
      dataIndex: 'adsActive',
      key: 'adsActive',
      width: 70,
      align: 'center' as const,
      hidden: filter.showAdsShipping !== 1,
      render: (adsActive: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat((adsActive || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: 'แอดไม่ติด',
      dataIndex: 'adsInactive',
      key: 'adsInactive',
      width: 90,
      align: 'center' as const,
      hidden: filter.showAdsShipping !== 1,
      render: (adsInactive: number) => (
        <Typography variant="subtitle2" color="red" textAlign={'center'}>
          {numberFormat((adsInactive || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: '%แอด',
      dataIndex: 'adsPercent',
      key: 'adsPercent',
      width: 70,
      align: 'center' as const,
      hidden: filter.showAdsShipping !== 1,
      render: (adsPercent: number) => (
        <Typography variant="subtitle2" textAlign={'center'}>
          {adsPercent.toFixed(2)}%
        </Typography>
      ),
    },
    {
      title: 'ค่าคอม',
      dataIndex: 'commission',
      key: 'commission',
      width: 70,
      align: 'center' as const,
      render: (commission: number, report: IReport) => {
        const comm = Number(report?.[commissionTypeName as keyof IReport] ?? 0)
        return (
          <Typography variant="subtitle2" color="red" textAlign={'center'}>
            {numberFormat(comm > 0 ? comm * -1 : comm)}
          </Typography>
        )
      },
    },
    {
      title: 'รายรับ',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      align: 'center' as const,
      render: (index: number) => (
        <Typography variant="subtitle2" color="green" textAlign={'center'}>
          {(reportsArray[index].bankTotal || 0) +
            (reportsArray[index].codTotal || 0)
            ? (
              (reportsArray[index].bankTotal || 0) +
              (reportsArray[index].codTotal || 0)
            ).toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'รายจ่าย',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      align: 'center' as const,
      render: (index: number, report: IReport) => {
        const taxAndReturnCost =
          ((reportsArray[index].total || 0) *
            (merchant?.config?.taxPercent || 0)) /
          100 +
          ((merchant?.config?.returnPercent || 0) * (report.codTotal || 0)) /
          100
        const codPercentInRow =
          ((merchant?.config?.codPercent || 0) * (report.codTotal || 0)) / 100
        const shippingCostInRow =
          shippingCost === 1
            ? (reportsArray[index].count || 0) *
            (merchant?.config.shippingCostFix || 0)
            : 0
        let amount =
          (reportsArray[index].costTotal || 0) +
          Number(report?.[commissionTypeName as keyof IReport] ?? 0) +
          (expense === 1 ? reportsArray[index].costAmountTotal || 0 : 0) +
          shippingCostInRow
        if (calTax === '1') {
          amount += taxAndReturnCost
        }
        if (codPercent == 1) {
          amount += Math.abs(codPercentInRow)
        }
        return (
          <Typography variant="subtitle2" color="red" textAlign={'center'}>
            {numberFormat(amount * -1)}
          </Typography>
        )
      },
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      align: 'center' as const,
      render: (index: number, report: IReport) => {
        const taxAndReturnCost =
          ((reportsArray[index].total || 0) *
            (merchant?.config?.taxPercent || 0)) /
          100 +
          ((merchant?.config?.returnPercent || 0) * (report.codTotal || 0)) /
          100

        const codPercentInRow =
          ((merchant?.config?.codPercent || 0) * (report.codTotal || 0)) / 100

        const shippingCostInRow =
          shippingCost === 1
            ? (reportsArray[index].count || 0) *
            (merchant?.config.shippingCostFix || 0)
            : 0

        let amount =
          (reportsArray[index].bankTotal || 0) +
          (reportsArray[index].codTotal || 0) -
          ((reportsArray[index].costTotal || 0) +
            (expense === 1 ? reportsArray[index].costAmountTotal || 0 : 0) +
            Number(report?.[commissionTypeName as keyof IReport] ?? 0) +
            shippingCostInRow)

        if (calTax === '1') {
          amount -= taxAndReturnCost
        }
        if (codPercent == 1) {
          amount += -Math.abs(codPercentInRow)
        }
        return (
          <Typography
            variant="subtitle2"
            color={amount > 0 ? 'green' : 'red'}
            textAlign={'center'}
          >
            {numberFormat(amount)}
          </Typography>
        )
      },
    },
  ].filter((item) => !item?.hidden)

  return (
    <>
      {isOpen && (
        <DailySalesReportOrderTableModal
          isOpen={isOpen}
          filter={filter}
          onClose={() => setIsOpen(false)}
          date={date}
        />
      )}
      {/* <CircularLoading isLoading={props.isLoading} /> */}
      <div ref={ref}>
        <Table
          loading={props.isLoading}
          size={'middle'}
          columns={columns}
          scroll={{ x: columns.reduce((pre, cur) => cur.width + pre, 0) }}
          dataSource={reportsArray.map((report: IReport, index: number) => ({
            ...report,
            index,
          }))}
          pagination={false}
          bordered
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    รวม
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Typography
                    variant="subtitle2"
                    color="green"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumOrder ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumUnit ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Typography
                    variant="subtitle2"
                    color="green"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumCodTotal ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumCodCount ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9}>
                  <Typography
                    variant="subtitle2"
                    color="green"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumBankTotal ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumBankCount ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10}>
                  <Box display={'flex'} justifyContent={'center'} gap={0.5}>
                    <Typography
                      variant="subtitle2"
                      color="green"
                      textAlign={'center'}
                    >
                      {numberFormat(totalUpsale ?? 0)}
                    </Typography>
                    <div>/</div>
                    <Typography
                      variant="subtitle2"
                      color="red"
                      textAlign={'center'}
                    >
                      {numberFormat(totalUnsuccessUpsale ?? 0)}
                    </Typography>
                  </Box>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumTax ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumShippingCostFix ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumPercentCod ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumReturnPercent ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {user?.isNotAllowSeeCost ? '-' : numberFormat((totalSumCostTotal ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12}>
                  <Typography
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumCostAmountTotal ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>
                {filter.showAdsShipping === 1 && (
                  <>
                    <Table.Summary.Cell index={14}>
                      <Typography
                        title="ค่าขนส่ง"
                        variant="subtitle2"
                        color="red"
                        textAlign={'center'}
                      >
                        -{numberFormat(totalSumShipping ?? 0 * -1)}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={15}>
                      <Typography
                        title="แอดติด"
                        variant="subtitle2"
                        color="red"
                        textAlign={'center'}
                      >
                        -{numberFormat(totalSumAdsActive ?? 0 * -1)}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={16}>
                      <Typography
                        title="แอดไม่ติด"
                        variant="subtitle2"
                        color="red"
                        textAlign={'center'}
                      >
                        -{numberFormat(totalSumAdsInactive ?? 0 * -1)}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={16}>
                      <Typography
                        title="%แอด"
                        variant="subtitle2"
                        textAlign={'center'}
                      >
                        {totalAdsPercent.toFixed(2)}%
                      </Typography>
                    </Table.Summary.Cell>
                  </>
                )}
                <Table.Summary.Cell index={13}>
                  <Typography
                    variant="subtitle2"
                    title="ค่าคอม"
                    color="red"
                    textAlign={'center'}
                  >
                    {numberFormat((totalSumCommission ?? 0) * -1)}
                  </Typography>
                </Table.Summary.Cell>

                <Table.Summary.Cell index={14}>
                  <Typography
                    title="รายรับ"
                    variant="subtitle2"
                    color="green"
                    textAlign={'center'}
                  >
                    {numberFormat(totalSumSubTotal ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={15}>
                  <Typography
                    title="รายจ่าย"
                    variant="subtitle2"
                    color="red"
                    textAlign={'center'}
                  >
                    -{numberFormat(totalSumCost ?? 0)}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={17}>
                  <Typography
                    title="คงเหลือ"
                    variant="subtitle2"
                    color={totalSumTotalAll > 0 ? 'green' : 'red'}
                    textAlign={'center'}
                  >
                    {numberFormat(
                      totalSumTotalAll > 0
                        ? totalSumTotalAll
                        : totalSumTotalAll * -1,
                    )}
                  </Typography>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />
      </div>
    </>
  )
})

export default DailySalesReportTable
